import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Download, FileText, Ban, MessageSquare } from "lucide-react";

// =================== Coupons ===================
export function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, min_cart_value: 0, max_discount: null, usage_limit: 0, per_user_limit: 1, scope: "all", is_active: true });
  const reload = () => api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/coupons", { ...form, value: Number(form.value), min_cart_value: Number(form.min_cart_value), usage_limit: Number(form.usage_limit), per_user_limit: Number(form.per_user_limit), max_discount: form.max_discount ? Number(form.max_discount) : null }); toast.success("Coupon created"); reload(); setForm({ code: "", type: "percent", value: 10, min_cart_value: 0, max_discount: null, usage_limit: 0, per_user_limit: 1, scope: "all", is_active: true }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (code) => { if (!window.confirm(`Delete coupon ${code}?`)) return; try { await api.delete(`/coupons/${code}`); reload(); } catch { toast.error("Failed"); } };
  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-6">
      <div className="bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30"><tr><th className="text-left p-3">Code</th><th className="text-left p-3">Type</th><th className="text-left p-3">Value</th><th className="text-left p-3">Used</th><th className="text-left p-3">Active</th><th></th></tr></thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.code} className="border-t border-brand-line" data-testid={`coupon-${c.code}`}>
                <td className="p-3 font-mono font-medium">{c.code}</td>
                <td className="p-3 capitalize">{c.type}</td>
                <td className="p-3">{c.type === "percent" ? `${c.value}%` : fmtINR(c.value)}</td>
                <td className="p-3">{c.used_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                <td className="p-3"><span className={`jp-badge ${c.is_active ? "bg-brand-primary text-white" : ""}`}>{c.is_active ? "Active" : "Off"}</span></td>
                <td className="p-3 text-right"><button onClick={() => del(c.code)} className="text-red-700"><Trash2 size={14}/></button></td>
              </tr>
            ))}
            {coupons.length === 0 && <tr><td colSpan={6} className="text-center p-12 text-brand-muted">No coupons yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <form onSubmit={submit} className="bg-white border border-brand-line p-5 h-fit space-y-3" data-testid="new-coupon-form">
        <h3 className="font-serif text-lg">New Coupon</h3>
        <input required placeholder="CODE" className="jp-input uppercase" value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})}/>
        <select className="jp-input" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})}>
          <option value="percent">Percent off</option>
          <option value="flat">Flat ₹ off</option>
        </select>
        <input required type="number" min={0} placeholder="Value" className="jp-input" value={form.value} onChange={(e) => setForm({...form, value: e.target.value})}/>
        <input type="number" min={0} placeholder="Min cart value" className="jp-input" value={form.min_cart_value} onChange={(e) => setForm({...form, min_cart_value: e.target.value})}/>
        {form.type === "percent" && <input type="number" min={0} placeholder="Max discount (₹)" className="jp-input" value={form.max_discount || ""} onChange={(e) => setForm({...form, max_discount: e.target.value})}/>}
        <input type="number" min={0} placeholder="Usage limit (0=∞)" className="jp-input" value={form.usage_limit} onChange={(e) => setForm({...form, usage_limit: e.target.value})}/>
        <input type="number" min={0} placeholder="Per-user limit" className="jp-input" value={form.per_user_limit} onChange={(e) => setForm({...form, per_user_limit: e.target.value})}/>
        <button type="submit" className="jp-btn-primary w-full"><Plus size={14} className="mr-2"/>Create</button>
      </form>
    </div>
  );
}

// =================== Banners ===================
export function BannersTab() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState({ title: "", subtitle: "", image_url: "", cta_text: "Shop Now", cta_link: "/products", display_order: 0, is_active: true });
  const reload = () => api.get("/banners/admin").then((r) => setBanners(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/banners", { ...form, display_order: Number(form.display_order || 0) }); toast.success("Banner created"); reload(); setForm({ title: "", subtitle: "", image_url: "", cta_text: "Shop Now", cta_link: "/products", display_order: 0, is_active: true }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete banner?")) return; try { await api.delete(`/banners/${id}`); reload(); } catch { toast.error("Failed"); } };
  const toggle = async (b) => { try { await api.put(`/banners/${b.id}`, { ...b, is_active: !b.is_active }); reload(); } catch { toast.error("Failed"); } };
  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-6">
      <div className="space-y-3">
        {banners.map((b) => (
          <div key={b.id} className="bg-white border border-brand-line flex items-center gap-4 p-3" data-testid={`banner-${b.id}`}>
            <img src={b.image_url} alt="" className="h-16 w-24 object-cover"/>
            <div className="flex-1">
              <p className="font-medium">{b.title}</p>
              <p className="text-xs text-brand-muted">{b.subtitle}</p>
              <p className="text-xs text-brand-muted">→ {b.cta_link}</p>
            </div>
            <div className="text-xs text-brand-muted">#{b.display_order}</div>
            <button onClick={() => toggle(b)} className="text-xs jp-badge">{b.is_active ? "On" : "Off"}</button>
            <button onClick={() => del(b.id)} className="text-red-700"><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
      <form onSubmit={submit} className="bg-white border border-brand-line p-5 h-fit space-y-3" data-testid="new-banner-form">
        <h3 className="font-serif text-lg">New Banner</h3>
        <input required placeholder="Title" className="jp-input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}/>
        <input placeholder="Subtitle" className="jp-input" value={form.subtitle} onChange={(e) => setForm({...form, subtitle: e.target.value})}/>
        <input required placeholder="Image URL" className="jp-input" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})}/>
        <input placeholder="CTA text" className="jp-input" value={form.cta_text} onChange={(e) => setForm({...form, cta_text: e.target.value})}/>
        <input placeholder="CTA link" className="jp-input" value={form.cta_link} onChange={(e) => setForm({...form, cta_link: e.target.value})}/>
        <input type="number" placeholder="Display order" className="jp-input" value={form.display_order} onChange={(e) => setForm({...form, display_order: e.target.value})}/>
        <button type="submit" className="jp-btn-primary w-full"><Plus size={14} className="mr-2"/>Create</button>
      </form>
    </div>
  );
}

// =================== Customers (CRM) ===================
export function CustomersTab() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const reload = () => api.get(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { reload(); }, [q]);
  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-3">
        <h3 className="font-serif text-xl">Customers</h3>
        <input placeholder="Search name / email / phone" className="jp-input border border-brand-line px-3 py-2 text-sm max-w-sm" value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-search"/>
      </div>
      <div className="bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30"><tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email / Phone</th><th className="text-right p-3">Orders</th><th className="text-right p-3">LTV</th><th className="text-left p-3">Status</th><th></th></tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.user_id} className="border-t border-brand-line" data-testid={`customer-${c.user_id}`}>
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3 text-brand-muted">{c.email}<br/><span className="text-xs">{c.phone || "—"}</span></td>
                <td className="p-3 text-right">{c.orders_count}</td>
                <td className="p-3 text-right">{fmtINR(c.lifetime_value)}</td>
                <td className="p-3">{c.blocked ? <span className="jp-badge bg-red-700 text-white">Blocked</span> : <span className="jp-badge">Active</span>}</td>
                <td className="p-3 text-right"><button onClick={() => setOpen(c.user_id)} className="text-brand-primary text-xs uppercase">View</button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="text-center p-12 text-brand-muted">No customers found.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <CustomerDrawer user_id={open} onClose={() => { setOpen(null); reload(); }}/>}
    </div>
  );
}

function CustomerDrawer({ user_id, onClose }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const reload = () => api.get(`/customers/${user_id}`).then((r) => setData(r.data));
  useEffect(() => { reload(); }, [user_id]);
  if (!data) return null;
  const c = data.customer;
  const addNote = async () => { if (!note.trim()) return; try { await api.post("/customers/notes", { user_id, note }); setNote(""); reload(); toast.success("Note added"); } catch { toast.error("Failed"); } };
  const toggleBlock = async () => { try { await api.post("/customers/block", { user_id, blocked: !data.blocked }); reload(); toast.success(data.blocked ? "Unblocked" : "Blocked"); } catch { toast.error("Failed"); } };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <aside className="bg-white w-full max-w-2xl h-full overflow-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="customer-drawer">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="font-serif text-2xl">{c.name}</h2>
            <p className="text-sm text-brand-muted">{c.email} • {c.phone || "no phone"}</p>
          </div>
          <button onClick={toggleBlock} className={`jp-btn-outline text-xs ${data.blocked ? "text-emerald-700 border-emerald-700" : "text-red-700 border-red-700"}`} data-testid="block-toggle">
            <Ban size={14} className="mr-1"/>{data.blocked ? "Unblock" : "Block"}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center mb-6">
          <div className="border border-brand-line p-3"><p className="font-serif text-xl">{data.orders.length}</p><p className="text-xs text-brand-muted">Orders</p></div>
          <div className="border border-brand-line p-3"><p className="font-serif text-xl">{fmtINR(data.orders.reduce((s, o) => s + (o.total || 0), 0))}</p><p className="text-xs text-brand-muted">LTV</p></div>
          <div className="border border-brand-line p-3"><p className="font-serif text-xl">{data.reviews.length}</p><p className="text-xs text-brand-muted">Reviews</p></div>
        </div>
        <h3 className="font-serif text-lg mb-2">Orders</h3>
        <div className="space-y-2 mb-6">
          {data.orders.slice(0, 10).map((o) => (
            <Link to={`/orders/${o.id}`} key={o.id} className="block border border-brand-line p-3 text-sm hover:border-brand-primary">
              <div className="flex justify-between"><span>{o.order_number}</span><span>{fmtINR(o.total)}</span></div>
              <p className="text-xs text-brand-muted">{o.status} • {new Date(o.created_at).toLocaleDateString("en-IN")}</p>
            </Link>
          ))}
        </div>
        <h3 className="font-serif text-lg mb-2">Internal notes</h3>
        <div className="space-y-2 mb-3">
          {(data.admin_notes || []).map((n) => (
            <div key={n.id} className="bg-brand-blush/30 border border-brand-line p-3 text-sm">
              <p>{n.note}</p>
              <p className="text-xs text-brand-muted mt-1">— {n.by} • {new Date(n.at).toLocaleString("en-IN")}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="jp-input flex-1" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input"/>
          <button onClick={addNote} className="jp-btn-primary px-3 py-2 text-xs"><MessageSquare size={14}/></button>
        </div>
      </aside>
    </div>
  );
}

// =================== Finance ===================
export function FinanceTab() {
  const [sub, setSub] = useState("cod");
  return (
    <div>
      <div className="flex gap-2 mb-5">
        {[["cod", "COD Reconciliation"], ["payouts", "Seller Payouts"], ["gst", "GST Report"]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} className={`px-3 py-2 text-xs uppercase tracking-wider ${sub === k ? "bg-brand-primary text-white" : "border border-brand-line"}`} data-testid={`finance-sub-${k}`}>{l}</button>
        ))}
      </div>
      {sub === "cod" && <CodReconcile/>}
      {sub === "payouts" && <SellerPayouts/>}
      {sub === "gst" && <GstReport/>}
    </div>
  );
}

function CodReconcile() {
  const [data, setData] = useState({ orders: [], summary: {} });
  const reload = () => api.get("/finance/cod").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const toggle = async (order_id, field, val) => {
    try { await api.post("/finance/cod/update", { order_id, [field]: val }); reload(); }
    catch { toast.error("Failed"); }
  };
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[["Total Due", data.summary.total_due], ["Collected", data.summary.total_collected], ["Deposited", data.summary.total_deposited], ["Outstanding", data.summary.outstanding]].map(([l, v]) => (
          <div key={l} className="bg-white border border-brand-line p-4"><p className="text-xs text-brand-muted uppercase">{l}</p><p className="font-serif text-xl mt-1">{fmtINR(v || 0)}</p></div>
        ))}
      </div>
      <div className="bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30"><tr><th className="text-left p-3">Order</th><th className="text-left p-3">Customer</th><th className="text-right p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-left p-3">Collected</th><th className="text-left p-3">Deposited</th></tr></thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id} className="border-t border-brand-line">
                <td className="p-3 font-medium">{o.order_number}</td>
                <td className="p-3">{o.customer_name}</td>
                <td className="p-3 text-right">{fmtINR(o.total)}</td>
                <td className="p-3"><span className="jp-badge capitalize">{o.status}</span></td>
                <td className="p-3"><input type="checkbox" checked={!!o.cod_collected} onChange={(e) => toggle(o.id, "collected", e.target.checked)} data-testid={`cod-collected-${o.id}`}/></td>
                <td className="p-3"><input type="checkbox" checked={!!o.cod_deposited} onChange={(e) => toggle(o.id, "deposited", e.target.checked)} data-testid={`cod-deposited-${o.id}`}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function SellerPayouts() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const reload = () => api.get("/finance/payouts").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const settle = async (sid, status) => { try { await api.post("/finance/payouts/status", { seller_id: sid, status }); reload(); toast.success("Updated"); } catch { toast.error("Failed"); } };
  return (
    <>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[["Gross", data.summary.total_gross], ["Commission", data.summary.total_commission], ["Net Payable", data.summary.total_net]].map(([l, v]) => (
          <div key={l} className="bg-white border border-brand-line p-4"><p className="text-xs text-brand-muted uppercase">{l}</p><p className="font-serif text-xl mt-1">{fmtINR(v || 0)}</p></div>
        ))}
      </div>
      <div className="bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30"><tr><th className="text-left p-3">Seller</th><th className="text-right p-3">Orders</th><th className="text-right p-3">Gross</th><th className="text-right p-3">Commission</th><th className="text-right p-3">Net</th><th className="text-left p-3">Status</th><th></th></tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.seller_id} className="border-t border-brand-line" data-testid={`payout-${r.seller_id}`}>
                <td className="p-3">{r.business_name}</td>
                <td className="p-3 text-right">{r.orders_count}</td>
                <td className="p-3 text-right">{fmtINR(r.gross)}</td>
                <td className="p-3 text-right">{fmtINR(r.commission)} <span className="text-xs text-brand-muted">({r.commission_rate}%)</span></td>
                <td className="p-3 text-right font-medium">{fmtINR(r.net_payable)}</td>
                <td className="p-3 capitalize">{r.status}</td>
                <td className="p-3 text-right">
                  <select onChange={(e) => settle(r.seller_id, e.target.value)} defaultValue="" className="text-xs border border-brand-line p-1">
                    <option value="" disabled>Set…</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="settled">Settled</option>
                  </select>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={7} className="text-center p-12 text-brand-muted">No delivered orders yet — payouts appear after delivery.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function GstReport() {
  const [rows, setRows] = useState([]);
  const [xmlMonth, setXmlMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [downloading, setDownloading] = useState(false);
  useEffect(() => { api.get("/finance/gst").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const downloadXml = async (month) => {
    setDownloading(true);
    try {
      const r = await api.get(`/finance/gstr1-xml?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = `GSTR1-${month}-06BITPK7883H1ZH.xml`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`GSTR-1 XML downloaded for ${month}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No data for that month");
    } finally { setDownloading(false); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-white border border-brand-line p-4 flex flex-wrap items-end gap-3" data-testid="gstr1-export">
        <div>
          <label className="jp-eyebrow block mb-1">GSTR-1 XML Export</label>
          <p className="text-xs text-brand-muted">Generates a GSTN-compatible XML covering B2CS &amp; B2CL sections for the chosen month — ready to upload to the GST portal's offline utility.</p>
        </div>
        <input type="month" value={xmlMonth} onChange={(e) => setXmlMonth(e.target.value)} className="jp-input border border-brand-line px-3 py-2 text-sm" data-testid="gstr1-month"/>
        <button onClick={() => downloadXml(xmlMonth)} disabled={downloading} className="jp-btn-primary px-4 py-2 text-xs" data-testid="gstr1-download"><Download size={14} className="mr-1"/>{downloading ? "Generating…" : "Download GSTR-1 XML"}</button>
      </div>
      <div className="bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-blush/30"><tr><th className="text-left p-3">Month</th><th className="text-right p-3">Invoices</th><th className="text-right p-3">Total Sales</th><th className="text-right p-3">Taxable</th><th className="text-right p-3">CGST</th><th className="text-right p-3">SGST</th><th className="text-right p-3">IGST</th><th className="text-right p-3">Total GST</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month} className="border-t border-brand-line">
                <td className="p-3 font-medium">{r.month}</td>
                <td className="p-3 text-right">{r.invoice_count}</td>
                <td className="p-3 text-right">{fmtINR(r.total_sales)}</td>
                <td className="p-3 text-right">{fmtINR(r.taxable_value)}</td>
                <td className="p-3 text-right">{fmtINR(r.cgst)}</td>
                <td className="p-3 text-right">{fmtINR(r.sgst)}</td>
                <td className="p-3 text-right">{fmtINR(r.igst)}</td>
                <td className="p-3 text-right font-medium">{fmtINR(r.total_gst)}</td>
                <td className="p-3 text-right"><button onClick={() => downloadXml(r.month)} className="text-brand-primary text-xs uppercase" data-testid={`gstr1-row-${r.month}`}>XML</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="text-center p-12 text-brand-muted">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =================== Reports ===================
export function ReportsTab() {
  const [sub, setSub] = useState("sales");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/reports/${sub}`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [sub]);
  const exportCsv = async () => {
    try {
      const resp = await api.get(`/reports/${sub}?export=true`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `${sub}_report.csv`; a.click();
    } catch { toast.error("Export failed"); }
  };
  const types = [["sales", "Sales"], ["products", "Products"], ["customers", "Customers"], ["sellers", "Sellers"], ["inventory", "Inventory"], ["coupons", "Coupons"]];
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {types.map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} className={`px-3 py-2 text-xs uppercase tracking-wider ${sub === k ? "bg-brand-primary text-white" : "border border-brand-line"}`} data-testid={`report-${k}`}>{l}</button>
        ))}
        <button onClick={exportCsv} className="jp-btn-outline px-3 py-2 text-xs ml-auto" data-testid="report-export"><Download size={14} className="mr-1"/>CSV</button>
      </div>
      <div className="bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          {rows.length > 0 && (
            <thead className="bg-brand-blush/30"><tr>{Object.keys(rows[0]).map((k) => <th key={k} className="text-left p-3 capitalize">{k.replace(/_/g, " ")}</th>)}</tr></thead>
          )}
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-brand-line">
                {Object.values(r).map((v, j) => <td key={j} className="p-3">{typeof v === "boolean" ? (v ? "Yes" : "No") : (v === null ? "—" : String(v))}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="text-center p-12 text-brand-muted">No data yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
